import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, ChevronRight, CheckCircle, Target, Heart, Award, Dumbbell, Clock, BarChart3, Activity, AlertTriangle, MessageSquare, TrendingUp, AlertCircle as AlertCircleIcon, ServerCrash, Zap, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { supabase } from '@/lib/customSupabaseClient';
import {
  Accordion,
  AccordionItem,
} from "@/components/ui/accordion"
import ExerciseCard from '@/components/ExerciseCard';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';

const CHART_COLORS = {
  blue: 'hsl(220, 70%, 50%)',
  green: 'hsl(160, 60%, 45%)',
  orange: 'hsl(30, 80%, 55%)',
  purple: 'hsl(280, 65%, 60%)',
  pink: 'hsl(340, 75%, 55%)',
};

const PainTimelineChart = ({ data }) => {
    const chartData = data
        .filter(d => d.pain_level_reported !== null && d.pain_level_reported !== undefined)
        .map(d => ({
            name: `#${d.order_in_session}`,
            pain: d.pain_level_reported,
            exerciseName: d.exercise_name,
        }));

    if (chartData.length < 2) {
        return (
            <div className="text-center text-muted-foreground py-8">
                <p>Not enough data for a pain timeline.</p>
            </div>
        );
    }
    
    return (
        <ResponsiveContainer width="100%" height={150}>
            <AreaChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" className="text-xs" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                <YAxis domain={[0, 10]} allowDecimals={false} className="text-xs" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                <RechartsTooltip
                    contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '0.5rem',
                    }}
                    labelStyle={{ fontWeight: 'bold', color: 'hsl(var(--foreground))' }}
                    formatter={(value, name, props) => [`${value}/10`, `Pain (${props.payload.exerciseName})`]}
                />
                <defs>
                    <linearGradient id="painGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_COLORS.pink} stopOpacity={0.7}/>
                        <stop offset="95%" stopColor={CHART_COLORS.pink} stopOpacity={0}/>
                    </linearGradient>
                </defs>
                <Area type="monotone" dataKey="pain" stroke={CHART_COLORS.pink} fillOpacity={1} fill="url(#painGradient)" strokeWidth={2} />
            </AreaChart>
        </ResponsiveContainer>
    );
};


const KPIStat = ({ title, value, icon: Icon, unit, colorClass, text, tooltip }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <div className="bg-card rounded-lg border p-4 flex flex-col justify-between">
                <div className="flex justify-between items-center mb-2">
                    <p className="text-xs text-muted-foreground font-medium">{title}</p>
                    <Icon className={cn("h-4 w-4", colorClass)} />
                </div>
                <div>
                    <span className={cn("text-2xl font-bold", colorClass)}>{value}</span>
                    {unit && <span className="text-sm text-muted-foreground ml-1">{unit}</span>}
                    {text && <span className="text-2xl font-bold">{text}</span>}
                </div>
            </div>
        </TooltipTrigger>
        {tooltip && <TooltipContent><p>{tooltip}</p></TooltipContent>}
    </Tooltip>
);

const calculateAvg = (items, key) => {
    if (!items || items.length === 0) return 0;
    const validItems = items.map(item => item[key]).filter(val => val !== null && val !== undefined);
    if (validItems.length === 0) return 0;
    return (validItems.reduce((a, b) => a + b, 0) / validItems.length);
};

const calculateCompliance = (exercises) => {
    if (!exercises || exercises.length === 0) return 0;
    const totalSetsPrescribed = exercises.reduce((acc, ex) => acc + (ex.prescribed_sets || 0), 0);
    const totalSetsCompleted = exercises.reduce((acc, ex) => acc + (ex.sets_data?.length || 0), 0);
    if (totalSetsPrescribed === 0) return 100;
    return (totalSetsCompleted / totalSetsPrescribed) * 100;
};

const PageSkeleton = () => (
    <div className="container mx-auto py-8 px-4 max-w-7xl space-y-8">
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-4 rounded-full" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-4 rounded-full" />
        <Skeleton className="h-4 w-40" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-8 w-80" />
        <Skeleton className="h-5 w-64" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map(i => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-48 rounded-lg" />
      <div className="space-y-4">
        {[0, 1, 2].map(i => (
          <Skeleton key={i} className="h-20 rounded-lg" />
        ))}
      </div>
    </div>
);

const SessionDetail = () => {
    const { userId, sessionId } = useParams();
    const [exerciseDetails, setExerciseDetails] = useState([]);
    const [sessionData, setSessionData] = useState(null);
    const [userName, setUserName] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedItems, setExpandedItems] = useState([]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const { data: userData, error: userError } = await supabase
                    .rpc('staff_get_user_profiles', { p_user_ids: [userId] }).single();
                if (userError) throw new Error(`User fetch failed: ${userError.message}`);
                setUserName(userData.full_name);
                setUserEmail(userData.email);

                const { data: summaryData, error: summaryError } = await supabase
                    .from('user_training_sessions').select('*, sessions(name, description, primary_goal, secondary_goals, clinical_reasoning, rest_periods_seconds)').eq('id', sessionId).eq('user_id', userId).single();
                if (summaryError) throw new Error(`Session summary fetch failed: ${summaryError.message}`);
                setSessionData(summaryData);

                const { data: prescribedData, error: prescribedError } = await supabase
                    .from('session_exercises').select('*').eq('session_id', summaryData.session_id);
                if (prescribedError) throw new Error(`Prescribed exercises fetch failed: ${prescribedError.message}`);
                
                const { data: rpcData, error: rpcError } = await supabase.rpc('admin_get_training_session_details', { p_training_session_id: sessionId });
                if (rpcError) throw new Error(`Session details RPC failed: ${rpcError.message}`);

                const prevWorkoutPromises = prescribedData.map(ex => 
                    supabase.rpc('get_last_workout_for_exercise', {
                        p_user_id: userId,
                        p_exercise_id: ex.exercise_id
                    })
                );
                const prevWorkoutResults = await Promise.all(prevWorkoutPromises);

                const mergedDetails = prescribedData.map((prescribed, index) => {
                    const actual = rpcData.find(d => d.exercise_id === prescribed.exercise_id);
                    const prevWorkoutData = prevWorkoutResults[index].data || [];
                    const prevVolume = Array.isArray(prevWorkoutData) && prevWorkoutData.length > 0 ? prevWorkoutData.reduce((acc, set) => acc + ((set.actual_reps || 0) * (set.actual_value || 0)), 0) : 0;
                    const currentVolume = Array.isArray(actual?.sets_data) && actual.sets_data.length > 0 ? actual.sets_data.reduce((acc, set) => acc + ((set.reps || 0) * (set.weight || 0)), 0) : 0;

                    return {
                        ...prescribed, ...actual,
                        prescribed_sets: prescribed.sets,
                        prescribed_reps: prescribed.reps,
                        prescribed_weight: prescribed.weight_kg,
                        exercise_name: actual?.exercise_name || prescribed.exercise_id,
                        exercise_group: actual?.exercise_category || 'General',
                        sets_data: actual?.sets_data || [],
                        notes: actual?.exercise_notes || actual?.notes || '',
                        volume_trend: {
                            previous: prevVolume,
                            current: currentVolume,
                        }
                    };
                }).sort((a, b) => a.order_in_session - b.order_in_session);

                setExerciseDetails(mergedDetails);
                if (mergedDetails.length > 0) {
                  setExpandedItems([`exercise-${mergedDetails[0].id}`]);
                }

            } catch (err) {
                console.error("Data fetching error:", err);
                setError('Failed to load session details. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [userId, sessionId]);
    
    const handleExpandAll = () => {
        setExpandedItems(exerciseDetails.map(ex => `exercise-${ex.id}`));
    };

    const handleCollapseAll = () => {
        setExpandedItems([]);
    };
    
    const handleExportPDF = async () => {
        toast("Generating PDF...", { description: "Please wait while we create your session report." });

        try {
            const doc = new jsPDF('p', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 15;
            let currentY = 0;

            const addFooter = () => {
                const footerY = 270;
                doc.setDrawColor(229, 231, 235);
                doc.line(margin, footerY, pageWidth - margin, footerY);

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(10);
                doc.setTextColor(16, 185, 129);
                doc.text("Questions or Concerns?", margin, footerY + 8);

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
                doc.setTextColor(75, 85, 99);
                doc.text("Contact: team@birkensports.com", margin, footerY + 13);
                doc.text("Visit: https://www.mymovement.space", margin, footerY + 18);

                doc.setFontSize(8);
                doc.setTextColor(107, 114, 128);
                doc.text("MyMovement.Space powered by Birken Sports", (pageWidth / 2), footerY + 13, { align: 'center' });
                
                const generatedDate = new Date().toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' });
                doc.setFontSize(7);
                doc.text(`Report Generated: ${generatedDate}`, pageWidth - margin, footerY + 18, { align: 'right' });
            };
            
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(24);
            doc.setTextColor(16, 185, 129);
            doc.text("MyMovement.Space", margin, 20);

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(107, 114, 128);
            doc.text("powered by Birken Sports", margin, 26);

            doc.setFont('helvetica', 'italic');
            doc.setFontSize(10);
            doc.text("Your Space To Move, Perform and Grow.", margin, 32);

            doc.setDrawColor(229, 231, 235);
            doc.line(margin, 38, pageWidth - margin, 38);

            const sessionName = sessionData?.sessions?.name || 'Session';
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(20);
            doc.setTextColor(0, 0, 0);
            doc.text(sessionName, margin, 48);

            const sessionDate = new Date(sessionData?.started_at).toLocaleDateString('en-US', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
            });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(12);
            doc.setTextColor(107, 114, 128);
            doc.text(sessionDate, margin, 58);

            doc.setFillColor(239, 246, 255);
            doc.setDrawColor(191, 219, 254);
            doc.setLineWidth(0.5);
            doc.roundedRect(margin, 68, pageWidth - (margin * 2), 25, 3, 3, 'FD');

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(30, 64, 175);
            doc.text("ATHLETE INFORMATION", margin + 5, 76);

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(55, 65, 81);
            doc.text(`Name: ${userName || 'N/A'}`, margin + 5, 83);
            doc.text(`Email: ${userEmail || 'N/A'}`, margin + 5, 88);

            currentY = 105;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(14);
            doc.setTextColor(55, 65, 81);
            doc.text("PERFORMANCE SUMMARY", margin, currentY);
            currentY += 2;
            doc.setDrawColor(229, 231, 235);
            doc.setLineWidth(0.2);
            doc.line(margin, currentY, pageWidth - margin, currentY);
            currentY += 8;

            const kpiBoxWidth = (pageWidth - (margin * 2) - (5 * 2)) / 3;
            const kpiBoxHeight = 22;
            const kpiHorizontalSpacing = 5;
            const kpiVerticalSpacing = 5;
            const kpiRadius = 2;

            const colors = {
                green: [22, 163, 74],
                yellow: [234, 179, 8],
                orange: [249, 115, 22],
                red: [220, 38, 38],
                blue: [37, 99, 235],
                cyan: [8, 145, 178],
                gray: [107, 114, 128],
            };

            const getPdfRpeColor = (rpe) => rpe > 8 ? colors.red : rpe > 6 ? colors.orange : rpe > 4 ? colors.yellow : colors.green;
            const getPdfPainColor = (pain) => pain > 6 ? colors.red : pain > 3 ? colors.orange : pain > 0 ? colors.yellow : colors.green;
            const getPdfComplianceColor = (c) => c >= 90 ? colors.green : c >= 75 ? colors.yellow : colors.orange;

            const drawKpiBox = (x, y, label, value, unit, color) => {
                doc.setFillColor(249, 250, 251);
                doc.roundedRect(x, y, kpiBoxWidth, kpiBoxHeight, kpiRadius, kpiRadius, 'F');
                
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(107, 114, 128);
                doc.text(label, x + 5, y + 7);

                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(color[0], color[1], color[2]);
                const valueText = `${value}${unit}`;
                doc.text(valueText, x + 5, y + 16);
            };

            const kpiGrid = [
                { label: 'COMPLETION', value: kpiData.compliance, unit: '%', color: getPdfComplianceColor(kpiData.compliance) },
                { label: 'AVG PAIN', value: kpiData.avgPain, unit: '/10', color: getPdfPainColor(kpiData.avgPain) },
                { label: 'AVG RPE', value: kpiData.avgRpe, unit: '/10', color: getPdfRpeColor(kpiData.avgRpe) },
                { label: 'AVG FORM', value: kpiData.avgForm, unit: '/10', color: colors.blue },
                { label: 'EXERCISES', value: `${kpiData.completedCount}/${kpiData.totalCount}`, unit: '', color: colors.cyan },
            ];

            kpiGrid.forEach((kpi, index) => {
                const col = index % 3;
                const row = Math.floor(index / 3);
                const x = margin + col * (kpiBoxWidth + kpiHorizontalSpacing);
                const y = currentY + row * (kpiBoxHeight + kpiVerticalSpacing);
                drawKpiBox(x, y, kpi.label, kpi.value, kpi.unit, kpi.color);
            });

            currentY += Math.ceil(kpiGrid.length / 3) * (kpiBoxHeight + kpiVerticalSpacing);
            
            currentY += 10;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(14);
            doc.setTextColor(55, 65, 81);
            doc.text("PAIN PROGRESSION", margin, currentY);
            currentY += 2;
            doc.setDrawColor(229, 231, 235);
            doc.setLineWidth(0.2);
            doc.line(margin, currentY, pageWidth - margin, currentY);
            currentY += 8;

            try {
                const chartElement = document.querySelector('.recharts-wrapper');
                if (chartElement) {
                    const canvas = await html2canvas(chartElement, {
                        scale: 2,
                        backgroundColor: '#ffffff'
                    });
                    const imgData = canvas.toDataURL('image/png');
                    const imgWidth = 180;
                    const imgHeight = (canvas.height * imgWidth) / canvas.width;

                    if (currentY + imgHeight > pageHeight - margin) {
                        addFooter();
                        doc.addPage();
                        currentY = margin;
                    }
                    
                    doc.addImage(imgData, 'PNG', margin, currentY, imgWidth, imgHeight);
                    currentY += imgHeight;
                } else {
                    doc.setFontSize(9);
                    doc.setTextColor(107, 114, 128);
                    doc.text("Pain timeline chart not available.", margin, currentY);
                    currentY += 10;
                }
            } catch(chartError) {
                console.error("Error capturing chart:", chartError);
                doc.setFontSize(9);
                doc.setTextColor(220, 38, 38);
                doc.text("Could not capture pain chart.", margin, currentY);
                currentY += 10;
            }

            if (sessionData.session_notes && sessionData.session_notes.trim()) {
                currentY += 10;
                if (currentY > pageHeight - 40) {
                    addFooter();
                    doc.addPage();
                    currentY = margin;
                }
                
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(14);
                doc.setTextColor(55, 65, 81);
                doc.text("SESSION NOTES", margin, currentY);
                currentY += 8;

                const notesLines = doc.splitTextToSize(sessionData.session_notes, 180);
                const notesBoxHeight = (notesLines.length * 5) + 12;

                if (currentY + notesBoxHeight > pageHeight - margin) {
                    addFooter();
                    doc.addPage();
                    currentY = margin;
                }

                doc.setFillColor(254, 252, 232);
                doc.setDrawColor(253, 230, 138);
                doc.setLineWidth(0.3);
                doc.roundedRect(margin, currentY, pageWidth - (margin * 2), notesBoxHeight, 3, 3, 'FD');

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
                doc.setTextColor(120, 53, 15);
                doc.text(notesLines, margin + 5, currentY + 8);

                currentY += notesBoxHeight;
            }
            addFooter();

            doc.addPage();
            currentY = margin;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.setTextColor(55, 65, 81);
            doc.text("EXERCISE BREAKDOWN", margin, currentY);
            currentY += 10;

            Object.entries(groupedExercises).forEach(([group, exercises]) => {
                if (currentY + 10 > pageHeight - margin) {
                    addFooter();
                    doc.addPage();
                    currentY = margin;
                }
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.setTextColor(75, 85, 99);
                doc.text(group.toUpperCase(), margin, currentY);
                currentY += 6;

                exercises.forEach(ex => {
                    const boxHeight = 25;
                    if (currentY + boxHeight > pageHeight - margin) {
                        addFooter();
                        doc.addPage();
                        currentY = margin;
                    }

                    doc.setFillColor(249, 250, 251);
                    doc.roundedRect(margin, currentY, pageWidth - (margin * 2), boxHeight, 2, 2, 'F');

                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(10);
                    doc.setTextColor(0, 0, 0);
                    doc.text(`#${ex.order_in_session} ${ex.exercise_name}`, margin + 3, currentY + 6);

                    let metricX = margin + 3;
                    const metricY = currentY + 13;
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(8);

                    const setsText = `Sets: ${ex.sets_data?.length || 0}/${ex.prescribed_sets || 'N/A'}`;
                    doc.setTextColor(colors.gray[0], colors.gray[1], colors.gray[2]);
                    doc.text(setsText, metricX, metricY);
                    metricX += doc.getTextWidth(setsText) + 8;

                    const painText = `Pain: ${ex.pain_level_reported ?? 'N/A'}/10`;
                    const painColor = ex.pain_level_reported != null ? getPdfPainColor(ex.pain_level_reported) : colors.gray;
                    doc.setTextColor(painColor[0], painColor[1], painColor[2]);
                    doc.text(painText, metricX, metricY);
                    metricX += doc.getTextWidth(painText) + 8;

                    const rpeText = `RPE: ${ex.rpe_reported ?? 'N/A'}/10`;
                    const rpeColor = ex.rpe_reported != null ? getPdfRpeColor(ex.rpe_reported) : colors.gray;
                    doc.setTextColor(rpeColor[0], rpeColor[1], rpeColor[2]);
                    doc.text(rpeText, metricX, metricY);
                    metricX += doc.getTextWidth(rpeText) + 8;
                    
                    const formText = `Form: ${ex.form_rating ?? 'N/A'}/10`;
                    doc.setTextColor(colors.gray[0], colors.gray[1], colors.gray[2]);
                    doc.text(formText, metricX, metricY);

                    if (ex.notes && ex.notes.trim()) {
                        doc.setFont('helvetica', 'italic');
                        doc.setFontSize(7);
                        doc.setTextColor(107, 114, 128);
                        const noteText = ex.notes.length > 120 ? ex.notes.substring(0, 120) + '...' : ex.notes;
                        doc.text(noteText, margin + 3, currentY + 20);
                    }
                    
                    currentY += boxHeight + 3;
                });
                currentY += 5;
            });
            addFooter();

            doc.addPage();
            currentY = margin;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.setTextColor(55, 65, 81);
            doc.text("SESSION SUMMARY", margin, currentY);
            currentY += 10;
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(55, 65, 81);
            
            const summaryItems = [
                `Compliance: ${kpiData.compliance}% (${getComplianceLabel(kpiData.compliance)})`,
                `Pain Management: ${getPainManagementLabel(kpiData.avgPain)} (Avg: ${kpiData.avgPain}/10)`,
                `Form Quality: ${getFormLabel(kpiData.avgForm)} (Avg: ${kpiData.avgForm}/10)`,
                `Intensity Level: ${getIntensityLabel(kpiData.sessionRpe)} (${kpiData.sessionRpe || 'N/A'}/10 RPE)`
            ];
            summaryItems.forEach(item => {
                doc.text(`• ${item}`, margin, currentY);
                currentY += 7;
            });
            
            currentY += 10;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.setTextColor(55, 65, 81);
            doc.text("COACHING INSIGHTS & RECOMMENDATIONS", margin, currentY);
            currentY += 8;

            let recommendationsMade = false;

            const drawRecommendationBox = (icon, text, bgColor, borderColor) => {
                const textLines = doc.splitTextToSize(text, 170);
                const boxHeight = (textLines.length * 5) + 15;
                if (currentY + boxHeight > pageHeight - 40) {
                  addFooter();
                  doc.addPage();
                  currentY = margin;
                }
                doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
                doc.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
                doc.setLineWidth(0.3);
                doc.roundedRect(margin, currentY, pageWidth - (margin * 2), boxHeight, 3, 3, 'FD');
                
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(18);
                doc.text(icon, margin + 5, currentY + 10);
                
                doc.setFontSize(9);
                doc.setTextColor(55, 65, 81);
                doc.text(textLines, margin + 15, currentY + 8);
                
                currentY += boxHeight + 5;
                recommendationsMade = true;
            };

            const highPainExercises = exerciseDetails.filter(ex => ex.pain_level_reported > 5);
            const lowFormExercises = exerciseDetails.filter(ex => ex.form_rating !== null && ex.form_rating < 6);
            const readyForProgression = kpiData.compliance >= 90 && parseFloat(kpiData.avgPain) <= 2 && parseFloat(kpiData.avgForm) >= 8;

            if (highPainExercises.length > 0) {
                const names = highPainExercises.map(e => e.exercise_name).join(', ');
                drawRecommendationBox("⚠️", `High pain detected during ${names}. Recommend load reduction or movement modification. Follow up on pain patterns in next session.`, [254, 242, 242], [252, 165, 165]);
            }

            if (kpiData.compliance < 75) {
                drawRecommendationBox("📊", `Compliance at ${kpiData.compliance}%. Discuss barriers with athlete. Consider program adjustment or additional support to improve adherence.`, [255, 251, 235], [252, 211, 77]);
            }

            if (lowFormExercises.length > 0) {
                const names = lowFormExercises.map(e => e.exercise_name).join(', ');
                drawRecommendationBox("🎯", `Form needs attention on ${names}. Schedule technique review or provide video feedback for correction.`, [254, 252, 232], [253, 230, 138]);
            }
            
            if (readyForProgression) {
                drawRecommendationBox("✅", `Excellent performance across all metrics. Athlete ready for progression. Consider increasing load by 5-10% or advancing exercise complexity.`, [240, 253, 244], [187, 247, 208]);
            }

            if (!recommendationsMade) {
                drawRecommendationBox("→", "Session shows steady progress. Continue current programming while monitoring pain levels and form quality.", [239, 246, 255], [191, 219, 254]);
            }

            addFooter();

            const date = new Date().toISOString().split('T')[0];
            const filename = `SessionReport_${sessionName.replace(/\s+/g, '_')}_${date}.pdf`;

            doc.save(filename);

            toast.success("PDF downloaded successfully!");
        } catch (error) {
            console.error("Error generating PDF:", error);
            toast.error("Something went wrong", { description: "There was a problem generating the PDF." });
        }
    };
    
    const groupedExercises = useMemo(() => {
        if (!exerciseDetails) return {};
        return exerciseDetails.reduce((acc, ex) => {
            const group = ex.exercise_group || 'Other';
            if (!acc[group]) acc[group] = [];
            acc[group].push(ex);
            return acc;
        }, {});
    }, [exerciseDetails]);

    const kpiData = useMemo(() => {
        if (!exerciseDetails || !sessionData) return null;
        const completedExercises = exerciseDetails.filter(ex => ex.completed_at);
        
        return {
            compliance: calculateCompliance(exerciseDetails).toFixed(0),
            avgRpe: calculateAvg(completedExercises, 'rpe_reported').toFixed(1),
            avgPain: calculateAvg(completedExercises, 'pain_level_reported').toFixed(1),
            avgForm: calculateAvg(completedExercises, 'form_rating').toFixed(1),
            completedCount: completedExercises.length,
            totalCount: exerciseDetails.length,
            duration: sessionData.duration_seconds ? Math.round(sessionData.duration_seconds / 60) : 0,
            sessionRpe: sessionData.session_rpe,
        };
    }, [exerciseDetails, sessionData]);

    const clinicalAlerts = useMemo(() => {
        if (!exerciseDetails) return [];
        return exerciseDetails.flatMap(ex => {
            const alerts = [];
            if (ex.pain_level_reported > 5) alerts.push({ type: 'pain', message: `High pain during ${ex.exercise_name} (${ex.pain_level_reported}/10)` });
            if (ex.form_rating !== null && ex.form_rating < 6) alerts.push({ type: 'form', message: `Poor form on ${ex.exercise_name} (${ex.form_rating}/10)` });
            return alerts;
        });
    }, [exerciseDetails]);

    const getRpeColor = (rpe) => rpe > 8 ? 'text-red-500' : rpe > 6 ? 'text-orange-500' : rpe > 4 ? 'text-amber-500' : 'text-green-500';
    const getPainColor = (pain) => pain > 6 ? 'text-red-500' : pain > 3 ? 'text-orange-500' : pain > 0 ? 'text-amber-500' : 'text-green-500';
    const getComplianceLabel = (c) => c >= 90 ? "Excellent" : c >= 75 ? "Good" : "Needs Improvement";
    const getPainManagementLabel = (p) => p <= 2 ? "Well controlled" : p <= 5 ? "Managed" : "High levels";
    const getFormLabel = (f) => f >= 8 ? "Good form" : f >= 6 ? "Average form" : "Needs attention";
    const getIntensityLabel = (r) => r >= 8 ? "High" : r >= 5 ? "Moderate" : r > 0 ? "Low" : "Not rated";

    const Breadcrumb = () => (
        <nav className="flex items-center text-sm text-muted-foreground overflow-x-auto whitespace-nowrap">
            <Link to="/user-activity" className="hover:text-foreground transition-colors shrink-0">User Activity</Link>
            <ChevronRight className="h-4 w-4 mx-1 shrink-0" />
            <Link to={`/user-activity/${userId}`} className="hover:text-foreground transition-colors shrink-0">{userName || 'User'}</Link>
            <ChevronRight className="h-4 w-4 mx-1 shrink-0" />
            <span className="font-medium text-foreground truncate">{sessionData?.sessions?.name || 'Session'}</span>
        </nav>
    );

    if (loading) return <PageSkeleton />;

    if (error) {
        return (
            <Card className="border-destructive/50 max-w-2xl mx-auto my-10">
                <CardHeader className="text-center">
                    <ServerCrash className="mx-auto h-12 w-12 text-destructive mb-4" />
                    <CardTitle>Something Went Wrong</CardTitle>
                    <CardDescription>{error}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                    <Button asChild variant="outline">
                        <Link to={`/user-activity/${userId}`}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Go Back
                        </Link>
                    </Button>
                </CardContent>
            </Card>
        );
    }
    
    return (
        <>
            <Helmet>
                <title>{`${sessionData?.sessions?.name || 'Session'} - My Movement Space`}</title>
                <meta name="description" content={`Performance summary for ${userName || 'user'} on the ${sessionData?.sessions?.name || 'training session'}.`} />
            </Helmet>
            <div className="min-h-full">
                <div className="container mx-auto py-8 px-4 max-w-7xl">
                    <div className="mb-6 flex justify-between items-center">
                        <Breadcrumb />
                        <Button className="ml-4" onClick={handleExportPDF}>
                            <Download className="mr-2 h-4 w-4" />
                            Export Session Report
                        </Button>
                    </div>
                    
                    {!sessionData || !kpiData ? (
                        <Card className="text-center py-16">
                            <CardHeader>
                                <CardTitle>No Session Data</CardTitle>
                                <CardDescription>Could not load summary data for this session.</CardDescription>
                            </CardHeader>
                        </Card>
                    ) : (
                        <>
                            <header className="mb-8">
                                <h1 className="text-2xl font-semibold tracking-tight">{sessionData.sessions.name}</h1>
                                <p className="text-sm text-muted-foreground mt-1">Performance metrics and exercise breakdown for {userName}.</p>
                            </header>
                            
                            <div className="sticky top-0 z-10 bg-background py-4 mb-8 border-b">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                    <KPIStat title="Completion" value={kpiData.compliance} unit="%" icon={CheckCircle} colorClass="text-green-500" tooltip="Percentage of prescribed sets completed" />
                                    <KPIStat title="Avg RPE" value={kpiData.avgRpe} unit="/10" icon={Target} colorClass={getRpeColor(kpiData.avgRpe)} tooltip="Average Rate of Perceived Exertion" />
                                    <KPIStat title="Avg Pain" value={kpiData.avgPain} unit="/10" icon={Heart} colorClass={getPainColor(kpiData.avgPain)} tooltip="Average Pain Level Reported" />
                                    <KPIStat title="Avg Form" value={kpiData.avgForm} unit="/10" icon={Award} colorClass="text-blue-500" tooltip="Average Form Rating" />
                                </div>
                            </div>
                            
                            <div className="space-y-4 mb-8">
                                {clinicalAlerts.length > 0 && (
                                    <Card className="border-destructive/50">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="flex items-center text-base text-destructive">
                                                <AlertTriangle className="h-4 w-4 mr-2" />
                                                Clinical Alerts
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <ul className="list-disc list-inside space-y-1 text-sm text-destructive">
                                                {clinicalAlerts.map((alert, i) => <li key={i}>{alert.message}</li>)}
                                            </ul>
                                        </CardContent>
                                    </Card>
                                )}

                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center text-base">
                                            <Heart className="w-4 h-4 mr-2 text-red-500" />
                                            Pain Timeline
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <PainTimelineChart data={exerciseDetails} />
                                    </CardContent>
                                </Card>

                                {sessionData.session_notes && (
                                    <Card className="border-amber-500/30">
                                        <CardHeader className="pb-3">
                                            <CardTitle className="flex items-center text-base">
                                                <MessageSquare className="h-4 w-4 mr-2 text-amber-500" />
                                                Session Notes
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <p className="text-sm text-muted-foreground">{sessionData.session_notes}</p>
                                        </CardContent>
                                    </Card>
                                )}

                                {sessionData.sessions && (
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="flex items-center text-base">
                                                <Activity className="h-4 w-4 mr-2" />
                                                Clinical Context
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <div className="grid md:grid-cols-2 gap-6">
                                                <div>
                                                    <h2 className="text-sm font-medium text-muted-foreground mb-1">Primary Goal</h2>
                                                    <p className="text-sm text-foreground">{sessionData.sessions.primary_goal || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <h2 className="text-sm font-medium text-muted-foreground mb-1">Secondary Goals</h2>
                                                    {sessionData.sessions.secondary_goals?.length > 0 ? (
                                                        <ul className="list-disc list-inside text-sm text-foreground space-y-1">
                                                            {sessionData.sessions.secondary_goals.map((goal, i) => <li key={i}>{goal}</li>)}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-sm text-foreground">None</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <h2 className="text-sm font-medium text-muted-foreground mb-1">Clinical Reasoning</h2>
                                                <p className="text-sm text-foreground">{sessionData.sessions.clinical_reasoning || 'N/A'}</p>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                            
                            <Card className="mb-8">
                                <CardHeader>
                                    <div className="flex justify-between items-center">
                                        <CardTitle className="text-base">Exercise Breakdown</CardTitle>
                                        <div className="flex items-center gap-2">
                                            <Button variant="outline" size="sm" onClick={handleExpandAll}>
                                                <ChevronDown className="h-4 w-4 mr-1.5" />
                                                Expand All
                                            </Button>
                                            <Button variant="outline" size="sm" onClick={handleCollapseAll}>
                                                <ChevronUp className="h-4 w-4 mr-1.5" />
                                                Collapse All
                                            </Button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {Object.keys(groupedExercises).length > 0 ? (
                                        <Accordion type="multiple" value={expandedItems} onValueChange={setExpandedItems} className="w-full space-y-3 border-none">
                                            {Object.entries(groupedExercises).map(([group, exercises]) => (
                                                <div key={group} className="mb-8 last:mb-0">
                                                    <h2 className="text-sm font-medium text-muted-foreground mb-3 border-b pb-2">{group}</h2>
                                                    {exercises.map((ex) => (
                                                        <AccordionItem value={`exercise-${ex.id}`} key={ex.id} className="border-b-0 mb-3">
                                                            <ExerciseCard exercise={ex} index={exerciseDetails.findIndex(e => e.id === ex.id)} />
                                                        </AccordionItem>
                                                    ))}
                                                </div>
                                            ))}
                                        </Accordion>
                                    ) : (
                                        <div className="text-center py-16 text-muted-foreground bg-muted rounded-lg">
                                            <Dumbbell className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                                            <h3 className="text-lg font-semibold">No Exercises Logged</h3>
                                            <p className="text-sm">This session has no recorded exercise data.</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <div className="grid lg:grid-cols-2 gap-8">
                                <Card>
                                    <CardHeader><CardTitle className="text-base">Session Summary</CardTitle></CardHeader>
                                    <CardContent className="text-sm space-y-4">
                                        <p><span className="font-semibold">Compliance:</span> {kpiData.compliance}% ({getComplianceLabel(kpiData.compliance)})</p>
                                        <p><span className="font-semibold">Pain:</span> {getPainManagementLabel(kpiData.avgPain)} (Avg: {kpiData.avgPain}/10)</p>
                                        <p><span className="font-semibold">Form:</span> {getFormLabel(kpiData.avgForm)} (Avg: {kpiData.avgForm}/10)</p>
                                        <p><span className="font-semibold">Intensity:</span> {getIntensityLabel(kpiData.sessionRpe)} ({kpiData.sessionRpe || 'N/A'}/10 RPE)</p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader><CardTitle className="text-base">Trainer Recommendations</CardTitle></CardHeader>
                                    <CardContent className="text-sm space-y-3">
                                        <div className="flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><p>Excellent consistency on warm-up exercises. Keep it up!</p></div>
                                        <div className="flex items-start gap-3"><AlertCircleIcon className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" /><p>Monitor pain on squats. Consider reducing weight if pain persists above 4/10.</p></div>
                                        <div className="flex items-start gap-3"><TrendingUp className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" /><p>Ready to increase weight on deadlifts next session by 2.5-5kg if form remains solid.</p></div>
                                        <div className="flex items-start gap-3"><Target className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" /><p>Focus on full range of motion during shoulder press; avoid partial reps.</p></div>
                                    </CardContent>
                                </Card>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default SessionDetail;
